import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, Phone, Mail, Check, X, Edit2, MessageSquare, MoreVertical, PhoneCall } from "lucide-react";

const STATUS_CONFIG = {
  pending:   { label: "Pendente",   color: "bg-amber-100 text-amber-700 border-amber-200" },
  confirmed: { label: "Confirmada", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelada",  color: "bg-red-100 text-red-700 border-red-200" },
};
const SEATING = { interior: "Interior", exterior: "Exterior/Esplanada", sin_preferencia: "Sem preferência" };

export default function ReservationsList({ reservations, loading, onEdit, onUpdateStatus, onOpenActions }) {
  return (
    <div className="space-y-3">
      {loading && <p className="text-center text-gray-400 py-8">A carregar...</p>}
      {reservations.map(r => (
        <div key={r.id} className="bg-white rounded-xl border p-3 sm:p-4 hover:shadow-sm transition" data-testid={`reservation-${r.id.slice(0, 8)}`}>
          {/* Mobile layout */}
          <div className="sm:hidden space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900 truncate">{r.customer_name}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{r.customer_phone}</p>
              </div>
              <Badge className={`${STATUS_CONFIG[r.status]?.color || "bg-gray-100 text-gray-600"} text-xs shrink-0`}>
                {STATUS_CONFIG[r.status]?.label || r.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{r.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.time}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.guests}</span>
            </div>
            {r.notes && <p className="text-xs text-gray-500 truncate"><MessageSquare className="w-3 h-3 inline mr-1" />{r.notes}</p>}
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              {r.status === "pending" && (
                <>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 flex-1 text-xs" onClick={() => onUpdateStatus(r.id, "confirmed")} data-testid={`confirm-${r.id.slice(0, 8)}`}>
                    <Check className="w-3 h-3 mr-1" />Confirmar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-7 px-3 flex-1 text-xs" onClick={() => onUpdateStatus(r.id, "cancelled")} data-testid={`cancel-${r.id.slice(0, 8)}`}>
                    <X className="w-3 h-3 mr-1" />Cancelar
                  </Button>
                </>
              )}
              {(r.status === "confirmed" || r.status === "attended" || r.status === "no_show") && (
                <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 h-7 px-3 flex-1 text-xs" onClick={() => onOpenActions(r)} data-testid={`actions-${r.id.slice(0, 8)}`}>
                  <MoreVertical className="w-3 h-3 mr-1" />Ações
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 ml-auto" onClick={() => onEdit({ ...r })} data-testid={`edit-${r.id.slice(0, 8)}`}>
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          {/* Desktop layout */}
          <div className="hidden sm:flex items-center gap-4">
            <div className="flex-1 grid grid-cols-6 gap-4 items-center">
              <div>
                <p className="font-semibold text-gray-900">{r.customer_name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5"><Phone className="w-3 h-3" />{r.customer_phone}</div>
                {r.customer_email && <div className="flex items-center gap-2 text-xs text-gray-500"><Mail className="w-3 h-3" />{r.customer_email}</div>}
              </div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" /><span className="text-sm font-medium">{r.date}</span></div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" /><span className="text-sm font-medium">{r.time}</span></div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-gray-400" /><span className="text-sm font-medium">{r.guests} pess.</span></div>
              <div>
                <Badge className={`${STATUS_CONFIG[r.status]?.color || "bg-gray-100 text-gray-600"} text-xs`}>
                  {STATUS_CONFIG[r.status]?.label || r.status}
                </Badge>
                {r.source === "admin" && <p className="text-[10px] text-blue-500 mt-0.5 flex items-center gap-0.5"><PhoneCall className="w-2.5 h-2.5" />Manual</p>}
                {r.seating_preference && r.seating_preference !== "sin_preferencia" && <p className="text-[10px] text-gray-400 mt-0.5">{SEATING[r.seating_preference]}</p>}
                {r.notes && <p className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[120px]" title={r.notes}><MessageSquare className="w-2.5 h-2.5 inline mr-0.5" />{r.notes}</p>}
              </div>
              <div className="flex items-center gap-1.5 justify-end">
                {r.status === "pending" && (
                  <>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3" onClick={() => onUpdateStatus(r.id, "confirmed")} data-testid={`confirm-${r.id.slice(0, 8)}`}>
                      <Check className="w-3.5 h-3.5 mr-1" />Confirmar
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-8 px-3" onClick={() => onUpdateStatus(r.id, "cancelled")} data-testid={`cancel-${r.id.slice(0, 8)}`}>
                      <X className="w-3.5 h-3.5 mr-1" />Cancelar
                    </Button>
                  </>
                )}
                {(r.status === "confirmed" || r.status === "attended" || r.status === "no_show") && (
                  <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 h-8 px-3" onClick={() => onOpenActions(r)} data-testid={`actions-${r.id.slice(0, 8)}`}>
                    <MoreVertical className="w-3.5 h-3.5 mr-1" />Ações
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onEdit({ ...r })} data-testid={`edit-${r.id.slice(0, 8)}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
      {reservations.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-400">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-medium">Sem reservas</p>
        </div>
      )}
    </div>
  );
}
